import { Box, Button, Divider } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { CachePolicies } from "use-http";

import { NotesModal } from "components/detector/NotesModal/NotesModal";
import { JANZU_URL } from "config/config";
import { NO_RETRIES } from "config/constants";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useGetDisagreementPermissions } from "hooks/useGetDisagreementPermissions";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { Disagreement } from "types/disagreements";
import { PermissionDisagreements } from "utils/disagreementsUtils";

import { DisagreementsButtons } from "./DisagreementsButtons";
import { ImageQueryExample } from "./ImageQueryExample";
import { ResponseLabel } from "./ResponseLabel";

export interface DisagreementContentProps {
  isLargeMode: boolean;
  disagreementData: Disagreement;
  onFinishRelabel: () => void;
}

export function DisagreementContent(props: DisagreementContentProps) {
  const { isLargeMode, disagreementData, onFinishRelabel } = props;

  const permission = useGetDisagreementPermissions();
  const [openNotes, setOpenNotes] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);

  const ImageQueryUrl = `${JANZU_URL}reef-api/disagreements`;
  const {
    patch: overrideLabel,
    loading,
    response,
  } = useFetchCustom<Disagreement>(ImageQueryUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
    retries: NO_RETRIES,
    retryOn: async () => false,
  });

  const isClosed = disagreementData?.state === "CLOSE";

  const handleOpenNotes = () => {
    setOpenNotes(true);
  };
  const handleCloseNotes = () => {
    setOpenNotes(false);
  };

  useEffect(() => {
    if (isClosed) {
      enqueueSnackbar("This disagreement is closed.", { variant: "info" });
    }
  }, [isClosed]);

  const handleSubmitLabel = async (label: string) => {
    if (disagreementData.id) {
      try {
        await overrideLabel(`/${disagreementData.id}/answer`, {
          id: disagreementData.id,
          answer: label,
        });
        const resStatus = await response.status;
        if (resStatus === 200) {
          onFinishRelabel();
          enqueueSnackbar("Disagreement updated", { variant: "success" });
          return;
        }
        enqueueSnackbar("Error labeling this disagreement.", {
          variant: "error",
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleCloseDiscussion = async () => {
    if (disagreementData.id) {
      setConfirmModal(false);
      try {
        await overrideLabel(`/${disagreementData.id}/state`, {
          state: "CLOSE",
        });
        const resStatus = await response.status;
        if (resStatus === 200) {
          onFinishRelabel();
          enqueueSnackbar("Success", { variant: "success" });
          return;
        }
        enqueueSnackbar("Should be at least one answer to the disagreement to be able to close it.", {
          variant: "error",
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const detectorData: any = useMemo(
    () => ({
      id: disagreementData?.predictor?.id,
      name: disagreementData?.predictor?.name,
      label_prompt: disagreementData?.predictor?.label_prompt,
      mode: DetectorModes.BINARY,
    }),
    [disagreementData]
  );

  return (
    <Box id="disagreement-content" height="100%" width="100%">
      {isLargeMode && (
        <Box className="flex h-[49px] border-b-[2px] border-gray-300 border-solid justify-between items-center">
          <Box className="flex justify-start items-center gap-2">
            <Typography variant="subtitle" className="text-black">
              Query:
            </Typography>
            <Typography variant="subtitle" fontWeight="semibold" className="text-primary-darker">
              {disagreementData?.predictor?.label_prompt}
            </Typography>
          </Box>

          <Button
            id="notes-button"
            onClick={handleOpenNotes}
            sx={{
              borderRadius: "30px",
              background: "#E5E9ED",
              px: "10px",
              py: "5px",
              color: "black",
              textTransform: "none",
              fontSize: "12px",
            }}
          >
            Open Full Notes
          </Button>
          {openNotes && (
            <NotesModal
              detector={detectorData}
              ownerGroupId={disagreementData?.predictor?.owner?.id}
              ownerGroupName="User"
              openModal={openNotes}
              onClose={handleCloseNotes}
            />
          )}
        </Box>
      )}

      <Box className="w-full h-[calc(100%-50px)]">
        <Box
          className={`flex h-[calc(100%-170px)] ${
            isLargeMode ? "justify-center flex-col md:flex-row" : "flex-col"
          } gap-6 mb-[10px]`}
        >
          <ImageQueryExample
            answerOwner="labeler"
            isLargeMode={isLargeMode}
            imageQuery={disagreementData.challenged_answer}
          />

          <ImageQueryExample
            answerOwner="other"
            isLargeMode={isLargeMode}
            imageQuery={disagreementData.reference_answer}
          />
        </Box>

        {(disagreementData.labeler_answer !== null ||
          disagreementData.manager_answer !== null ||
          disagreementData.customer_answer !== null) && <Divider className="border-b-[2px]" />}

        <Box className={`flex h-[46px] items-center ${isLargeMode ? "justify-center gap-6" : "flex-col gap-2"}`}>
          {disagreementData.labeler_answer !== null && (
            <ResponseLabel
              type="Labeler"
              className={disagreementData.labeler_answer}
              currentClassNameAnswer={disagreementData.challenged_answer.class_name}
            />
          )}

          {disagreementData.customer_answer !== null && (
            <ResponseLabel
              type="Customer"
              className={disagreementData.customer_answer}
              currentClassNameAnswer={disagreementData.challenged_answer.class_name}
            />
          )}

          {disagreementData.manager_answer !== null && (
            <ResponseLabel
              type="Manager"
              className={disagreementData.manager_answer}
              currentClassNameAnswer={disagreementData.challenged_answer.class_name}
            />
          )}
        </Box>

        <Divider className="border-b-[2px] mb-[10px]" />

        <DisagreementsButtons
          isClosed={isClosed}
          loading={loading}
          showOptionsButtons={isLargeMode || permission === PermissionDisagreements.Labeler}
          onSubmit={handleSubmitLabel}
          currentClassNameAnswer={disagreementData.challenged_answer.class_name}
        />

        {permission === PermissionDisagreements.Manager && !isClosed && (
          <Box className="flex h-[50px] w-full justify-center items-center">
            <Button onClick={() => setConfirmModal(true)} className="w-auto bg-[#214FB3] text-white px-6">
              <Typography textTransform="uppercase" noWrap>
                {loading ? "Loading..." : "End discussion"}
              </Typography>
            </Button>
          </Box>
        )}

        {confirmModal && (
          <ConfirmDeleteModal
            isDeleteModalOpen={confirmModal}
            modalContent="<strong>This discussion will be closed</strong><br /> Are you sure you want to close it?"
            modalTitle="END DISCUSSION"
            setIsDeleteModalOpen={setConfirmModal}
            onRemoveHandler={handleCloseDiscussion}
          />
        )}
      </Box>
    </Box>
  );
}
